<template>
  <ReportPageLayout>
    <template #page-header>
      <OverviewNavHeader
        v-if="report && report.report_grades && report.report_grades.length > 0"
        :report="report"
      />
    </template>

    <!-- Default slot for main content -->
    <DataStateHandler
      :loading="loading"
      :error="error"
      :isEmpty="!loading && !report && !error"
    >
      <!-- Custom error state with Retry button -->
      <template #error="{ errorMessage }">
        <div class="error-container text-center py-8">
          <v-alert
            type="error"
            title="Error Loading Report"
            :text="errorMessage"
            class="mx-auto"
            max-width="600"
            variant="tonal"
            border="start"
          ></v-alert>
          <a
            href="#"
            class="btn btn-primary mt-4"
            @click.prevent="reloadReport"
          >
            Retry
          </a>
        </div>
      </template>

      <!-- Custom empty state -->
      <template #empty>
        <div class="text-center py-8">
          <v-alert
            type="info"
            title="No Report Data"
            text="No report data was found."
            class="mx-auto"
            max-width="600"
            variant="tonal"
          ></v-alert>
        </div>
      </template>

      <!-- Default Content (report loaded) -->
      <template #default>
        <v-responsive max-width="1320" class="mx-auto">
          <v-row>
            <v-col cols="12">
              <OverviewHeadingCard v-if="report" :report="report" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" lg="3">
              <!-- Navigation Sidebar -->
              <div v-if="!loading && report && report.slug">
                <ReportSideNav
                  :report="report"
                  mode="overview"
                  :hasScienceOfReading="hasScienceOfReadingData"
                />
              </div>
            </v-col>
            <v-col cols="12" lg="9">
              <div class="reports-overview-section">
                <div class="mb-4 text-secondary content-section-title">
                  Report Overview
                </div>
                <SeriesPerformanceSnapshot
                  :report="report"
                  class="mb-12"
                  id="ratings-snapshot-0"
                />

                <!-- Overview content comes from Grade Band Sections below, not from gateway_snapshots -->

                <!-- Grade Band Sections -->
                <div v-if="sortedOverviews.length > 1">
                  <!-- Multiple grade bands - show each separately -->
                  <div
                    v-for="(overview, overviewIndex) in sortedOverviews"
                    :key="`overview-${overviewIndex}`"
                    class="grade-band-section"
                  >
                    <div class="mt-6">
                      <SectionHeading
                        :title="overview.title || 'Overview'"
                        level="h2"
                        :id="`overview-${overviewIndex}`"
                      >
                      </SectionHeading>

                      <v-expand-transition>
                        <div>
                          <ExpandedContent :html="overview.summary || ''" />
                        </div>
                      </v-expand-transition>

                      <v-divider class="my-12"></v-divider>

                      <div :id="`gateway-ratings-summary-${overviewIndex}`">
                        <SummaryAlignmentUsability
                          :grades="getGradeBandSummaryGrades(overview)"
                        />
                      </div>

                      <template v-if="hasGradeBandScienceOfReading(overview)">
                        <v-divider class="my-12"></v-divider>
                        <div :id="`science-of-reading-${overviewIndex}`">
                          <ScienceOfReading
                            :report="report"
                            :loading="loading"
                            :overview="overview"
                          />
                        </div>
                      </template>

                      <template
                        v-if="
                          getGradeBandAdditionalCriteria(overview).length > 0
                        "
                      >
                        <div :id="`additional-criteria-${overviewIndex}`">
                          <SectionHeading
                            :title="getAdditionalElementsTitle(overview)"
                            level="h2"
                            titleClass="font-weight-bold text-secondary mt-16 text-h6"
                          />

                          <!-- Additional Criteria Table -->
                          <DataTable
                            :headers="getGradeBandTableHeaders(overview)"
                            :items="getGradeBandAdditionalCriteria(overview)"
                            :skillName="true"
                            classes="mb-16"
                          />
                        </div>
                      </template>
                    </div>

                    <!-- Add divider between grade bands except for the last one -->
                    <v-divider
                      v-if="overviewIndex < sortedOverviews.length - 1"
                      class="my-16"
                    ></v-divider>
                  </div>
                </div>

                <!-- Single grade band or fallback - show traditional layout -->
                <div v-else class="mt-6">
                  <div>
                    <SectionHeading
                      :title="overviewTitle"
                      level="h2"
                      id="overview-0"
                    >
                    </SectionHeading>

                    <v-expand-transition>
                      <div>
                        <ExpandedContent :html="overviewSummary || ''" />
                      </div>
                    </v-expand-transition>

                    <v-divider class="my-12"></v-divider>

                    <div id="gateway-ratings-summary-0">
                      <SummaryAlignmentUsability :grades="summaryGrades" />
                    </div>

                    <template v-if="hasScienceOfReadingData">
                      <v-divider class="my-12"></v-divider>
                      <div id="science-of-reading-0">
                        <ScienceOfReading :report="report" :loading="loading" />
                      </div>
                    </template>

                    <template v-if="additionalCriteriaItems.length > 0">
                      <div id="additional-criteria-0">
                        <SectionHeading
                          title="Additional Elements"
                          level="h2"
                          titleClass="font-weight-bold text-secondary mt-16 text-h6"
                        />

                        <!-- Additional Criteria Table -->
                        <DataTable
                          :headers="dynamicTableHeaders"
                          :items="additionalCriteriaItems"
                          :skillName="true"
                          classes="mb-16"
                        />
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-responsive>
      </template>
    </DataStateHandler>
  </ReportPageLayout>
</template>

<script setup lang="ts">
  import { computed, ref, watch, onMounted } from 'vue';
  import { useRoute } from 'vue-router';

  import { useDocumentTitle } from '../composables/useDocumentTitle';
  import { useGlossary } from '../composables/useGlossary';
  import { useReportLoader } from '../composables/useReportLoader';
  import {
    formatAdditionalElementsScore,
    formatGatewayScore,
  } from '../lib/status-utils';

  import ReportPageLayout from '../layouts/ReportPageLayout.vue';

  import DataStateHandler from '../components/shared/DataStateHandler.vue';
  import SectionHeading from '../components/shared/SectionHeading.vue';
  import ExpandedContent from '../components/shared/ExpandedContent.vue';
  import DataTable from '../components/shared/DataTable.vue';

  import SeriesPerformanceSnapshot from '../components/reports/SeriesPerformanceSnapshot.vue';
  import SummaryAlignmentUsability from '../components/reports/SummaryAlignmentUsability.vue';
  import OverviewNavHeader from '../components/reportOverview/OverviewNavHeader.vue';
  import ReportSideNav from '../components/shared/ReportSideNav.vue';
  import OverviewHeadingCard from '../components/reportOverview/OverviewHeadingCard.vue';
  import ScienceOfReading from '../components/reportOverview/ScienceOfReading.vue';
  import StatusIcon from '../components/common/StatusIcon.vue';

  import type { Report, GatewaySnapshotItem } from '../store';
  import { useReportsStore } from '../store';

  interface GatewayOverview {
    slug: string;
    label: string;
    name: string;
    title: string;
    score: string;
    review_status: {
      slug: string;
      name: string;
      title: string;
    };
    order: number;
    failed_non_negotiable: boolean;
    is_mll: boolean;
  }

  const route = useRoute();
  const { openGlossary } = useGlossary();
  const reportsStore = useReportsStore();

  const reportSlug = computed(() => route.params.id as string);
  const { report, loading, error, reloadReport } = useReportLoader(reportSlug);

  // Debug the report data when it changes
  watch(
    report,
    (newReport) => {
      console.log('ReportOverview: Report data changed:', {
        newReport,
        gateway_snapshots: newReport?.gateway_snapshots,
        gateway_snapshots_length: newReport?.gateway_snapshots?.length,
        slug: newReport?.slug,
        isProxy:
          newReport &&
          typeof newReport === 'object' &&
          newReport.constructor.name === 'ReactiveObjectImpl',
      });
    },
    { immediate: true, deep: true },
  );

  // Create a reactive ref for summary grades that can be updated asynchronously
  const summaryGrades = ref<
    Array<{
      name: string;
      reportUrl?: string;
      gateways: Array<{
        slug: string;
        label: string;
        name: string;
        title: string;
        score: string;
        review_status: {
          slug: string;
          name: string;
          title: string;
        };
        order: number;
        failed_non_negotiable: boolean;
        formattedScore: string;
      }>;
    }>
  >([]);

  // Helper function to decode HTML entities in scores
  const decodeHtmlEntities = (str: string): string => {
    if (!str) return str;
    const textArea = document.createElement('textarea');
    textArea.innerHTML = str;
    return textArea.value;
  };

  // Function to fetch and populate summary grades
  const fetchSummaryGrades = async () => {
    if (!report.value) {
      summaryGrades.value = [];
      return;
    }

    // Use report_grades for complete grade list (to get ALL grades)
    // But get gateway data consistently from gateway_snapshots (for standardization)
    if (
      !report.value?.report_grades ||
      !Array.isArray(report.value.report_grades)
    ) {
      // Fallback to overviews if no report_grades
      if (!report.value?.overviews?.[0]?.report_cards) {
        summaryGrades.value = [];
        return;
      }

      const reportCards = report.value.overviews[0].report_cards;
      const fallbackGrades = reportCards
        .map((rc) => {
          let gradeDisplayName =
            rc.grade.name || rc.grade.title || rc.grade.shortcode;
          if (rc.grade.shortcode === 'K') {
            gradeDisplayName = 'Kindergarten';
          } else if (rc.grade.shortcode && !isNaN(Number(rc.grade.shortcode))) {
            gradeDisplayName = rc.grade.name || `${rc.grade.shortcode} Grade`;
          }

          return {
            name: gradeDisplayName,
            reportUrl: `/reports/detail/${report.value?.slug}/${rc.grade.slug}`,
            gateways: rc.gateways
              .map((gateway) => ({
                ...gateway,
                label: gateway.label,
                score: decodeHtmlEntities(gateway.score || 'NE'),
                slug: gateway.slug,
                name: gateway.name,
                title: gateway.title,
                order: gateway.order || 0,
                failed_non_negotiable: gateway.failed_non_negotiable || false,
                review_status: gateway.review_status || {
                  slug: 'not-eligible',
                  name: 'Not Eligible',
                  title: 'Not Eligible',
                },
                formattedScore: formatGatewayScore(
                  decodeHtmlEntities(gateway.score || 'NE'),
                  gateway.review_status?.slug,
                ),
              }))
              .sort((a, b) => a.order - b.order),
          };
        })
        .sort((a, b) => {
          const orderA =
            report.value?.overviews?.[0]?.report_cards.find(
              (rc) =>
                rc.grade.name === a.name ||
                (rc.grade.shortcode === 'K' && a.name === 'Kindergarten'),
            )?.grade.order ?? 99;
          const orderB =
            report.value?.overviews?.[0]?.report_cards.find(
              (rc) =>
                rc.grade.name === b.name ||
                (rc.grade.shortcode === 'K' && b.name === 'Kindergarten'),
            )?.grade.order ?? 99;
          return orderA - orderB;
        });

      summaryGrades.value = fallbackGrades;
      return;
    }

    // STANDARDIZED APPROACH: Use report_grades for ALL grades, get gateway data from gateway_snapshots
    const allGrades = report.value.report_grades
      .map((gradeInfo) => {
        let gradeDisplayName =
          gradeInfo.name || gradeInfo.title || gradeInfo.shortcode;
        if (gradeInfo.shortcode === 'K') {
          gradeDisplayName = 'Kindergarten';
        } else if (gradeInfo.shortcode && !isNaN(Number(gradeInfo.shortcode))) {
          gradeDisplayName = gradeInfo.name || `${gradeInfo.shortcode} Grade`;
        }

        // Get gateway data from gateway_snapshots for consistency
        let gateways: Array<any> = [];

        if (
          report.value?.gateway_snapshots &&
          Array.isArray(report.value.gateway_snapshots)
        ) {
          gateways = report.value.gateway_snapshots
            .map((gatewaySnapshot) => {
              // Find score data for this grade
              const scoreData = gatewaySnapshot.scores.find(
                (score) =>
                  score.grade.slug === gradeInfo.slug ||
                  score.grade.shortcode === gradeInfo.shortcode,
              );

              if (scoreData) {
                return {
                  slug: gatewaySnapshot.slug,
                  label: gatewaySnapshot.label,
                  name: gatewaySnapshot.name,
                  title: gatewaySnapshot.title,
                  score: decodeHtmlEntities(scoreData.score || 'NE'),
                  order: gatewaySnapshot.order || 0,
                  failed_non_negotiable:
                    scoreData.failed_non_negotiable || false,
                  review_status: {
                    slug: scoreData.review_status || 'not-eligible',
                    name:
                      scoreData.review_status === 'meets'
                        ? 'Meets'
                        : scoreData.review_status === 'partially-meets'
                          ? 'Partially Meets'
                          : scoreData.review_status === 'does-not-meet'
                            ? 'Does Not Meet'
                            : 'Not Eligible',
                    title:
                      scoreData.review_status === 'meets'
                        ? 'Meets Expectations'
                        : scoreData.review_status === 'partially-meets'
                          ? 'Partially Meets Expectations'
                          : scoreData.review_status === 'does-not-meet'
                            ? 'Does Not Meet Expectations'
                            : 'Not Eligible',
                  },
                  formattedScore: formatGatewayScore(
                    decodeHtmlEntities(scoreData.score || 'NE'),
                    scoreData.review_status,
                  ),
                };
              } else {
                return null;
              }
            })
            .filter(
              (gateway): gateway is NonNullable<typeof gateway> =>
                gateway !== null,
            )
            .sort((a, b) => a.order - b.order);
        }

        return {
          name: gradeDisplayName,
          reportUrl: `/reports/detail/${report.value?.slug}/${gradeInfo.slug}`,
          gateways,
        };
      })
      .sort((a, b) => {
        const orderA =
          report.value?.report_grades?.find(
            (rg) =>
              rg.name === a.name ||
              (rg.shortcode === 'K' && a.name === 'Kindergarten'),
          )?.order ?? 99;
        const orderB =
          report.value?.report_grades?.find(
            (rg) =>
              rg.name === b.name ||
              (rg.shortcode === 'K' && b.name === 'Kindergarten'),
          )?.order ?? 99;
        return orderA - orderB;
      });

    summaryGrades.value = allGrades;
  };

  // Watch for changes to report data and fetch summary grades
  watch(
    () => report.value,
    () => {
      if (report.value) {
        fetchSummaryGrades();
      }
    },
    { immediate: true },
  );

  // Initial fetch on component mount
  onMounted(() => {
    if (report.value) {
      fetchSummaryGrades();
    }
  });

  const dynamicTableHeaders = computed(() => {
    // Use overviews[0].report_cards to only show grades that have reviewed data
    // This prevents showing columns full of N/A values
    if (!report.value?.overviews?.[0]?.report_cards) return [];
    const sortedReportCards = [...report.value.overviews[0].report_cards].sort(
      (a, b) => a.grade.order - b.grade.order,
    );
    return sortedReportCards.map((rc) => rc.grade.shortcode || rc.grade.slug);
  });

  const hasScienceOfReadingData = computed(() => {
    if (!report.value?.overviews?.[0]?.criteria_groups) return false;

    const sorGroup = report.value.overviews[0].criteria_groups.find(
      (cg) =>
        cg.title.includes('Science of Reading') ||
        cg.slug === 'science-of-reading',
    );

    return sorGroup && sorGroup.criteria && sorGroup.criteria.length > 0;
  });

  const additionalCriteriaItems = computed(() => {
    if (
      !report.value?.overviews?.[0]?.criteria_groups ||
      dynamicTableHeaders.value.length === 0
    )
      return [];

    const additionalGroup = report.value.overviews[0].criteria_groups.find(
      (cg) =>
        cg.title.includes('Additional Elements') ||
        cg.title.includes('Additional Criteria') ||
        cg.slug === 'additional-criteria' ||
        cg.slug === 'additional-elements',
    );

    if (!additionalGroup) return [];

    // Filter out "Intentional Design" from Additional Elements table
    return additionalGroup.criteria
      .filter((criterion) => criterion.label !== 'Intentional Design')
      .map((criterion) => {
        const item: { [key: string]: any } = {
          skill: criterion.label,
          subtitle:
            criterion.non_negotiable === true ||
            String(criterion.non_negotiable) === 'Non-negotiable'
              ? 'Non-negotiable'
              : '',
        };

        dynamicTableHeaders.value.forEach((gradeShortCode) => {
          const scoreEntries = criterion.scores.filter(
            (s) =>
              s.grade.shortcode === gradeShortCode ||
              s.grade.slug === gradeShortCode,
          );

          if (scoreEntries.length > 0) {
            let bestScore = scoreEntries[0];

            // First, prioritize "not-claimed", "not-eligible", or "did-not-review" entries
            for (const entry of scoreEntries) {
              if (
                typeof entry.review_status === 'string'
                  ? entry.review_status === 'not-claimed' ||
                    entry.review_status === 'not-eligible' ||
                    entry.review_status === 'did-not-review'
                  : entry.review_status?.slug === 'not-claimed' ||
                    entry.review_status?.slug === 'not-eligible' ||
                    entry.review_status?.slug === 'did-not-review'
              ) {
                bestScore = entry;
                break;
              }
            }

            // If no "not-claimed" entries found, look for valid scored entries
            if (
              !(typeof bestScore.review_status === 'string'
                ? bestScore.review_status === 'not-claimed' ||
                  bestScore.review_status === 'not-eligible' ||
                  bestScore.review_status === 'did-not-review'
                : bestScore.review_status?.slug === 'not-claimed' ||
                  bestScore.review_status?.slug === 'not-eligible' ||
                  bestScore.review_status?.slug === 'did-not-review')
            ) {
              for (const entry of scoreEntries) {
                const decodedScore = decodeHtmlEntities(entry.score || '');
                if (
                  decodedScore !== '0%' &&
                  (typeof entry.review_status === 'string'
                    ? entry.review_status !== 'not-claimed' &&
                      entry.review_status !== 'not-eligible' &&
                      entry.review_status !== 'did-not-review'
                    : entry.review_status?.slug !== 'not-claimed' &&
                      entry.review_status?.slug !== 'not-eligible' &&
                      entry.review_status?.slug !== 'did-not-review')
                ) {
                  bestScore = entry;
                  break;
                }
              }
            }

            if (bestScore) {
              const reviewStatus =
                typeof bestScore.review_status === 'string'
                  ? bestScore.review_status
                  : bestScore.review_status?.slug;

              if (reviewStatus) {
                if (bestScore.score) {
                  const decodedScore = decodeHtmlEntities(bestScore.score);
                  item[gradeShortCode] = formatAdditionalElementsScore(
                    decodedScore,
                    reviewStatus,
                  );
                } else {
                  item[gradeShortCode] = formatAdditionalElementsScore(
                    '',
                    reviewStatus,
                  );
                }
              } else {
                item[gradeShortCode] = 'N/A';
              }
            } else {
              item[gradeShortCode] = 'N/A';
            }
          } else {
            item[gradeShortCode] = 'N/A';
          }
        });
        return item;
      })
      .sort((a, b) => {
        const criterionA = additionalGroup.criteria.find(
          (c) => c.label === a.skill,
        );
        const criterionB = additionalGroup.criteria.find(
          (c) => c.label === b.skill,
        );
        if (
          criterionA?.order !== undefined &&
          criterionB?.order !== undefined
        ) {
          return criterionA.order - criterionB.order;
        }
        return a.skill && b.skill ? a.skill.localeCompare(b.skill) : 0;
      });
  });

  // New computed properties for grade band handling
  const sortedOverviews = computed(() => {
    if (!report.value?.overviews || !Array.isArray(report.value.overviews)) {
      return [];
    }

    // Filter out overviews with null or empty report_cards, then sort by lowest grade order
    return [...report.value.overviews]
      .filter(
        (overview) =>
          overview.report_cards &&
          Array.isArray(overview.report_cards) &&
          overview.report_cards.length > 0,
      )
      .sort((a, b) => {
        const aLowestOrder = Math.min(
          ...a.report_cards.map((rc) => rc.grade.order),
        );
        const bLowestOrder = Math.min(
          ...b.report_cards.map((rc) => rc.grade.order),
        );
        return aLowestOrder - bLowestOrder;
      });
  });

  const sortedGateways = computed(() => {
    if (
      !report.value?.gateway_snapshots ||
      !Array.isArray(report.value.gateway_snapshots)
    ) {
      return [] as GatewaySnapshotItem[];
    }

    // Sort by order
    return [...report.value.gateway_snapshots].sort(
      (a, b) => a.order - b.order,
    );
  });

  const getGatewayStatusColor = (gateway: GatewaySnapshotItem): string => {
    const firstScore = gateway.scores[0];
    if (!firstScore) return 'info';

    if (firstScore.review_status === 'meets') {
      return 'success';
    } else if (firstScore.review_status === 'partially-meets') {
      return 'warning';
    } else if (firstScore.review_status === 'does-not-meet') {
      return 'error';
    } else {
      return 'info';
    }
  };

  const getGatewayStatusText = (gateway: GatewaySnapshotItem): string => {
    const firstScore = gateway.scores[0];
    if (!firstScore) return 'Not Available';

    switch (firstScore.review_status) {
      case 'meets':
        return 'Meets Expectations';
      case 'partially-meets':
        return 'Partially Meets Expectations';
      case 'does-not-meet':
        return 'Does Not Meet Expectations';
      default:
        return 'Not Available';
    }
  };

  // New methods for grade band specific data
  const getGradeBandSummaryGrades = (overview: any) => {
    if (!overview?.report_cards) return [];

    return overview.report_cards
      .map((reportCard: any) => {
        let gradeDisplayName =
          reportCard.grade.name ||
          reportCard.grade.title ||
          reportCard.grade.shortcode;
        if (reportCard.grade.shortcode === 'K') {
          gradeDisplayName = 'Kindergarten';
        } else if (
          reportCard.grade.shortcode &&
          !isNaN(Number(reportCard.grade.shortcode))
        ) {
          gradeDisplayName =
            reportCard.grade.name || `${reportCard.grade.shortcode} Grade`;
        }

        return {
          name: gradeDisplayName,
          reportUrl: `/reports/detail/${report.value?.slug}/${reportCard.grade.slug}`,
          gateways: reportCard.gateways
            .map((gateway: any) => ({
              ...gateway,
              label: gateway.label,
              score: decodeHtmlEntities(gateway.score || 'NE'),
              slug: gateway.slug,
              name: gateway.name,
              title: gateway.title,
              order: gateway.order || 0,
              failed_non_negotiable: gateway.failed_non_negotiable || false,
              review_status: gateway.review_status || {
                slug: 'not-eligible',
                name: 'Not Eligible',
                title: 'Not Eligible',
              },
              formattedScore: formatGatewayScore(
                decodeHtmlEntities(gateway.score || 'NE'),
                gateway.review_status?.slug,
              ),
            }))
            .sort((a: any, b: any) => a.order - b.order),
        };
      })
      .sort((a: any, b: any) => {
        const orderA =
          overview.report_cards.find(
            (rc: any) =>
              rc.grade.name === a.name ||
              (rc.grade.shortcode === 'K' && a.name === 'Kindergarten'),
          )?.grade.order ?? 99;
        const orderB =
          overview.report_cards.find(
            (rc: any) =>
              rc.grade.name === b.name ||
              (rc.grade.shortcode === 'K' && b.name === 'Kindergarten'),
          )?.grade.order ?? 99;
        return orderA - orderB;
      });
  };

  const hasGradeBandScienceOfReading = (overview: any) => {
    if (!overview?.criteria_groups) return false;

    const sorGroup = overview.criteria_groups.find(
      (cg: any) =>
        cg.title.includes('Science of Reading') ||
        cg.slug === 'science-of-reading',
    );

    return sorGroup && sorGroup.criteria && sorGroup.criteria.length > 0;
  };

  const getAdditionalElementsTitle = (overview: any) => {
    if (!overview?.criteria_groups) return 'Additional Elements';

    const additionalGroup = overview.criteria_groups.find(
      (cg: any) =>
        cg.title.includes('Additional Elements') ||
        cg.title.includes('Additional Criteria') ||
        cg.slug === 'additional-criteria' ||
        cg.slug === 'additional-elements',
    );

    return additionalGroup?.title || 'Additional Elements';
  };

  const getGradeBandTableHeaders = (overview: any) => {
    if (!overview?.report_cards) return [];
    const sortedReportCards = [...overview.report_cards].sort(
      (a: any, b: any) => a.grade.order - b.grade.order,
    );
    return sortedReportCards.map(
      (rc: any) => rc.grade.shortcode || rc.grade.slug,
    );
  };

  const getGradeBandAdditionalCriteria = (overview: any) => {
    if (!overview?.criteria_groups) return [];

    const headers = getGradeBandTableHeaders(overview);
    if (headers.length === 0) return [];

    const additionalGroup = overview.criteria_groups.find(
      (cg: any) =>
        cg.title.includes('Additional Elements') ||
        cg.title.includes('Additional Criteria') ||
        cg.slug === 'additional-criteria' ||
        cg.slug === 'additional-elements',
    );

    if (!additionalGroup) return [];

    // Filter out "Intentional Design" from Additional Elements table
    return additionalGroup.criteria
      .filter((criterion: any) => criterion.label !== 'Intentional Design')
      .map((criterion: any) => {
        const item: { [key: string]: any } = {
          skill: criterion.label,
          subtitle:
            criterion.non_negotiable === true ||
            String(criterion.non_negotiable) === 'Non-negotiable'
              ? 'Non-negotiable'
              : '',
        };

        headers.forEach((gradeShortCode: string) => {
          const scoreEntries = criterion.scores.filter(
            (s: any) =>
              s.grade.shortcode === gradeShortCode ||
              s.grade.slug === gradeShortCode,
          );

          if (scoreEntries.length > 0) {
            let bestScore = scoreEntries[0];

            // First, prioritize "not-claimed", "not-eligible", or "did-not-review" entries
            for (const entry of scoreEntries) {
              if (
                typeof entry.review_status === 'string'
                  ? entry.review_status === 'not-claimed' ||
                    entry.review_status === 'not-eligible' ||
                    entry.review_status === 'did-not-review'
                  : entry.review_status?.slug === 'not-claimed' ||
                    entry.review_status?.slug === 'not-eligible' ||
                    entry.review_status?.slug === 'did-not-review'
              ) {
                bestScore = entry;
                break;
              }
            }

            // If no "not-claimed" entries found, look for valid scored entries
            if (
              !(typeof bestScore.review_status === 'string'
                ? bestScore.review_status === 'not-claimed' ||
                  bestScore.review_status === 'not-eligible' ||
                  bestScore.review_status === 'did-not-review'
                : bestScore.review_status?.slug === 'not-claimed' ||
                  bestScore.review_status?.slug === 'not-eligible' ||
                  bestScore.review_status?.slug === 'did-not-review')
            ) {
              for (const entry of scoreEntries) {
                const decodedScore = decodeHtmlEntities(entry.score || '');
                if (
                  decodedScore !== '0%' &&
                  (typeof entry.review_status === 'string'
                    ? entry.review_status !== 'not-claimed' &&
                      entry.review_status !== 'not-eligible' &&
                      entry.review_status !== 'did-not-review'
                    : entry.review_status?.slug !== 'not-claimed' &&
                      entry.review_status?.slug !== 'not-eligible' &&
                      entry.review_status?.slug !== 'did-not-review')
                ) {
                  bestScore = entry;
                  break;
                }
              }
            }

            if (bestScore) {
              const reviewStatus =
                typeof bestScore.review_status === 'string'
                  ? bestScore.review_status
                  : bestScore.review_status?.slug;

              if (reviewStatus) {
                if (bestScore.score) {
                  const decodedScore = decodeHtmlEntities(bestScore.score);
                  item[gradeShortCode] = formatAdditionalElementsScore(
                    decodedScore,
                    reviewStatus,
                  );
                } else {
                  item[gradeShortCode] = formatAdditionalElementsScore(
                    '',
                    reviewStatus,
                  );
                }
              } else {
                item[gradeShortCode] = 'N/A';
              }
            } else {
              item[gradeShortCode] = 'N/A';
            }
          } else {
            item[gradeShortCode] = 'N/A';
          }
        });
        return item;
      })
      .sort((a: any, b: any) => {
        const criterionA = additionalGroup.criteria.find(
          (c: any) => c.label === a.skill,
        );
        const criterionB = additionalGroup.criteria.find(
          (c: any) => c.label === b.skill,
        );
        if (
          criterionA?.order !== undefined &&
          criterionB?.order !== undefined
        ) {
          return criterionA.order - criterionB.order;
        }
        return a.skill && b.skill ? a.skill.localeCompare(b.skill) : 0;
      });
  };

  const overviewTitle = computed(() => {
    if (report.value?.overviews?.[0]?.title) {
      return report.value.overviews[0].title;
    } else {
      return 'Report Overview';
    }
  });

  const overviewSummary = computed(() => {
    if (report.value?.overviews?.[0]?.summary) {
      return report.value.overviews[0].summary;
    } else {
      return '';
    }
  });

  const documentTitleState = computed(() => {
    if (loading.value) {
      return 'Loading Report...';
    } else if (error.value) {
      return 'Error Loading Report';
    } else if (report.value && report.value.title) {
      return report.value.title;
    } else {
      return 'Report Overview';
    }
  });
  useDocumentTitle(documentTitleState);
</script>

<style lang="scss">
  @use '../styles/abstracts/variables' as *;

  .v-responsive {
    padding-right: 0;
  }

  .content-section-title {
    font-family: 'rockwell', sans-serif !important;
    font-size: 38px !important;
    font-weight: normal !important;
  }

  .performance-snapshot {
    background: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .performance-table {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;

    :deep(th) {
      font-weight: 600;
      color: rgba(0, 0, 0, 0.87);
      background: #f5f5f5;
    }

    :deep(td) {
      height: 48px;
    }
  }

  .data-table {
    thead th {
      border-bottom: 1px solid
        rgba(var(--v-border-color), var(--v-border-opacity));
      font-weight: $font-weight-bold !important;
      color: rgb(var(--v-theme-tonal-black));
      font-size: 18px;
      padding-top: 12px;
      padding-bottom: 12px;
      background-color: white;
    }

    thead th:first-child {
      padding: 0;
    }

    tbody {
      tr {
        background-color: white;

        &:nth-child(even) {
          background-color: #f0f3f5;
        }

        td {
          border-bottom: 1px solid
            rgba(var(--v-border-color), var(--v-border-opacity));
          vertical-align: middle;
          padding: 16px !important;
          font-size: 18px;
          color: rgb(var(--v-theme-text-secondary));
        }

        &:last-child td {
          border-bottom: none;
        }

        td:first-child {
          padding-left: 0;
          padding-right: 16px;
        }
      }
    }

    .skill-name {
      color: rgb(var(--v-theme-text-primary));
      font-size: 18px;
      margin-bottom: 2px;
    }

    .skill-subtitle {
      color: rgb(var(--v-theme-text-secondary));
      font-size: 14px;
    }

    .grade-header {
      padding-right: 16px;
    }
  }

  .grade-header {
    padding-right: 16px;
  }

  .grade-band-section {
    margin-bottom: 2rem;

    &:not(:last-child) {
      border-bottom: 1px solid
        rgba(var(--v-border-color), var(--v-border-opacity));
      padding-bottom: 2rem;
    }
  }

  .grade-band-section .v-divider {
    margin: 2rem 0 !important;
  }

  .gateway-section {
    background: #f5f5f5;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .gateway-content {
    display: flex;
    align-items: center;
  }
</style>
